import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox, Select,
  Layout, BlockStack, InlineStack, Box, Spinner, DataTable, Pagination, InlineGrid,
} from '@shopify/polaris';
import { DeleteIcon, PlusIcon, GiftCardIcon } from '@shopify/polaris-icons';
import { useSpinToWin } from '../../hooks/useSpinToWin';
import { MIN_SPIN_SEGMENTS, MAX_SPIN_SEGMENTS, type SpinToWinSettings, type SpinToWinSegment } from '../../api/spinToWin';
import { StatCard } from '../shared/StatCard';

const EMPTY_SEGMENT: SpinToWinSegment = { label: '', type: 'discount', discount_percent: 10, weight: 1 };

// Matches WHEEL_COLORS in extensions/spin-to-win/assets/spin-to-win.js so the
// preview looks exactly like what ships to the storefront.
const WHEEL_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

function segmentText(s: SpinToWinSegment): string {
  return s.type === 'discount' && s.discount_percent ? `${s.discount_percent}% OFF` : (s.label || '—');
}

function LivePreview({ settings }: { settings: SpinToWinSettings }) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<SpinToWinSegment | null>(null);

  const segments = settings.segments;
  const sliceAngle = segments.length ? 360 / segments.length : 0;
  const wheelBackground = segments
    .map((_, i) => `${WHEEL_COLORS[i % WHEEL_COLORS.length]} ${i * sliceAngle}deg ${(i + 1) * sliceAngle}deg`)
    .join(', ');

  const handleTestSpin = () => {
    if (spinning || segments.length < MIN_SPIN_SEGMENTS) return;
    setSpinning(true);
    setResult(null);
    const index = Math.floor(Math.random() * segments.length);
    const centerAngle = index * sliceAngle + sliceAngle / 2;
    const spins = 5 * 360;
    setRotation((r) => r + spins + ((360 - centerAngle) % 360));
    setTimeout(() => {
      setResult(segments[index]);
      setSpinning(false);
    }, 4000);
  };

  if (segments.length < MIN_SPIN_SEGMENTS) {
    return <Text as="p" tone="subdued">Add at least {MIN_SPIN_SEGMENTS} segments above to preview the wheel.</Text>;
  }

  return (
    <div style={{ background: 'rgba(17,17,27,0.65)', padding: 24, borderRadius: 8, display: 'flex', justifyContent: 'center' }}>
      <div style={{ background: 'linear-gradient(180deg, #ffffff, #fbfbfe)', borderRadius: 20, padding: '32px 26px 28px', maxWidth: 380, width: '100%', textAlign: 'center', boxShadow: '0 24px 60px rgba(17,17,27,0.35)' }}>
        <Text as="h3" variant="headingLg">{settings.headline}</Text>
        <Box paddingBlockStart="150" paddingBlockEnd="400">
          <Text as="p" tone="subdued">{settings.body}</Text>
        </Box>

        <div style={{ position: 'relative', width: 232, height: 232, margin: '0 auto 22px', padding: 8, borderRadius: '50%', background: 'linear-gradient(135deg, #FFD65A, #FF9F43 55%, #FFD65A)', boxShadow: '0 14px 32px rgba(0,0,0,0.22)' }}>
          <div
            aria-hidden
            style={{
              position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0, borderLeft: '11px solid transparent', borderRight: '11px solid transparent',
              borderTop: '20px solid #14142b', zIndex: 3, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))',
            }}
          />
          <div
            style={{
              position: 'relative', width: '100%', height: '100%', borderRadius: '50%',
              border: '4px solid #fff', overflow: 'hidden', boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.08)',
              background: `conic-gradient(${wheelBackground})`,
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.2, 1)' : undefined,
            }}
          >
            {segments.map((s, i) => {
              const centerAngle = i * sliceAngle + sliceAngle / 2;
              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute', top: '50%', left: '50%', width: '50%', height: 0,
                    transformOrigin: 'left center', transform: `rotate(${centerAngle}deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 12,
                  }}
                >
                  <span style={{ display: 'inline-block', color: '#fff', fontSize: 12, fontWeight: 800, textShadow: '0 1px 3px rgba(0,0,0,0.45)', whiteSpace: 'nowrap', transform: 'rotate(90deg)' }}>
                    {segmentText(s)}
                  </span>
                </div>
              );
            })}
          </div>
          <div
            aria-hidden
            style={{
              position: 'absolute', top: '50%', left: '50%', width: 34, height: 34, transform: 'translate(-50%, -50%)',
              background: '#fff', borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.3), 0 0 0 3px #FFD65A',
              zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}
          >
            ⭐
          </div>
        </div>

        {result ? (
          <BlockStack gap="200">
            <Text as="p" fontWeight="bold">
              {result.type === 'discount' ? `You won ${result.label}!` : result.label}
            </Text>
            {result.type === 'discount' && (
              <Box padding="200">
                <div style={{ padding: '12px 14px', border: '1.5px dashed #1D4ED8', borderRadius: 10, background: '#eef2ff', color: '#1D4ED8', fontSize: 14, fontWeight: 600 }}>
                  A real, unique discount code is minted here for the shopper.
                </div>
              </Box>
            )}
            <Button onClick={handleTestSpin}>Test again</Button>
          </BlockStack>
        ) : (
          <button
            type="button"
            onClick={handleTestSpin}
            disabled={spinning}
            style={{
              padding: '13px 18px', borderRadius: 10, background: 'linear-gradient(135deg, #1D4ED8, #4338CA)',
              color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: spinning ? 'default' : 'pointer',
              opacity: spinning ? 0.7 : 1, boxShadow: '0 6px 16px rgba(29,78,216,0.35)',
            }}
          >
            {spinning ? 'Spinning…' : settings.button_text}
          </button>
        )}
      </div>
    </div>
  );
}

export function SpinToWinPanel() {
  const {
    settings, entriesCount, entries, entriesPage, entriesLastPage,
    loading, saving, error, fetchSettings, save, fetchEntries, setError,
  } = useSpinToWin();

  const [form, setForm] = useState<SpinToWinSettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); fetchEntries(1); }, [fetchSettings, fetchEntries]);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  if (loading) {
    return (
      <Page fullWidth title="Spin-to-Win Popup">
        <Layout>
          <Layout.Section>
            <InlineStack align="center"><Spinner /></InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!form) {
    return (
      <Page fullWidth title="Spin-to-Win Popup">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
              <p>{error ?? 'Failed to load spin-to-win settings.'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const canSubmit = form.segments.length >= MIN_SPIN_SEGMENTS
    && form.segments.every((s) => s.label.trim() !== '' && (s.type !== 'discount' || (s.discount_percent ?? 0) > 0));

  const updateSegment = (index: number, patch: Partial<SpinToWinSegment>) => {
    setForm({
      ...form,
      segments: form.segments.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    });
  };

  const addSegment = () => {
    if (form.segments.length >= MAX_SPIN_SEGMENTS) return;
    setForm({ ...form, segments: [...form.segments, { ...EMPTY_SEGMENT }] });
  };

  const removeSegment = (index: number) => {
    if (form.segments.length <= MIN_SPIN_SEGMENTS) return;
    setForm({ ...form, segments: form.segments.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    setSaved(false);
    const result = await save(form);
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Spin-to-Win Popup"
      subtitle="A gamified discount wheel — each prize is a real, unique Shopify discount code, minted the moment someone wins it"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Settings saved. Changes appear on your storefront within about a minute.</p>
              </Banner>
            )}

            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
              <StatCard label="Total Spins" value={String(entriesCount)} icon={GiftCardIcon} iconColor="pink" />
              <StatCard
                label="Widget Status"
                value={form.enabled ? 'Enabled' : 'Disabled'}
                icon={GiftCardIcon}
                iconColor={form.enabled ? 'green' : 'indigo'}
              />
            </InlineGrid>

            <Card>
              <BlockStack gap="400">
                <Checkbox
                  label="Show the spin-to-win popup on my storefront"
                  checked={form.enabled}
                  onChange={(v) => setForm({ ...form, enabled: v })}
                />

                <TextField label="Headline" autoComplete="off" value={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
                <TextField label="Body text" autoComplete="off" multiline={2} value={form.body} onChange={(v) => setForm({ ...form, body: v })} />
                <TextField label="Button text" autoComplete="off" value={form.button_text} onChange={(v) => setForm({ ...form, button_text: v })} />
                <TextField
                  label="Show after (seconds)"
                  helpText="How long a shopper browses before the popup appears"
                  type="number"
                  autoComplete="off"
                  value={String(form.trigger_delay_seconds)}
                  onChange={(v) => setForm({ ...form, trigger_delay_seconds: Number(v) || 15 })}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Wheel Segments ({form.segments.length}/{MAX_SPIN_SEGMENTS})</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Each "Discount" segment gets its own real Shopify discount code, minted uniquely per winner. Weight
                  controls how likely a segment is to be picked — higher weight, more likely.
                </Text>

                <BlockStack gap="300">
                  {form.segments.map((segment, index) => (
                    <Card key={index} background="bg-surface-secondary">
                      <BlockStack gap="300">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="span" variant="bodySm" fontWeight="semibold">Segment {index + 1}</Text>
                          {form.segments.length > MIN_SPIN_SEGMENTS && (
                            <Button
                              icon={DeleteIcon}
                              variant="plain"
                              tone="critical"
                              accessibilityLabel={`Remove segment ${index + 1}`}
                              onClick={() => removeSegment(index)}
                            />
                          )}
                        </InlineStack>

                        <InlineStack gap="300" wrap>
                          <Box minWidth="180px">
                            <TextField label="Label" autoComplete="off" value={segment.label} onChange={(v) => updateSegment(index, { label: v })} />
                          </Box>
                          <Box minWidth="160px">
                            <Select
                              label="Type"
                              options={[
                                { label: 'Discount', value: 'discount' },
                                { label: 'No prize (try again)', value: 'no_prize' },
                              ]}
                              value={segment.type}
                              onChange={(v) => updateSegment(index, {
                                type: v as SpinToWinSegment['type'],
                                discount_percent: v === 'discount' ? (segment.discount_percent ?? 10) : null,
                              })}
                            />
                          </Box>
                          {segment.type === 'discount' && (
                            <Box minWidth="140px">
                              <TextField
                                label="Discount %"
                                type="number"
                                autoComplete="off"
                                value={String(segment.discount_percent ?? '')}
                                onChange={(v) => updateSegment(index, { discount_percent: Number(v) || null })}
                              />
                            </Box>
                          )}
                          <Box minWidth="120px">
                            <TextField
                              label="Weight"
                              helpText="Odds"
                              type="number"
                              autoComplete="off"
                              value={String(segment.weight)}
                              onChange={(v) => updateSegment(index, { weight: Number(v) || 1 })}
                            />
                          </Box>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  ))}
                </BlockStack>

                {form.segments.length < MAX_SPIN_SEGMENTS && (
                  <Box>
                    <Button icon={PlusIcon} onClick={addSegment}>Add segment</Button>
                  </Box>
                )}

                <Box>
                  <Button variant="primary" loading={saving} disabled={!canSubmit} onClick={handleSave}>Save Settings</Button>
                </Box>

                <Banner tone="info">
                  <p>
                    Changing a segment's discount percentage replaces its underlying Shopify discount automatically —
                    previously issued codes for that segment keep working, but new spins use the new percentage.
                  </p>
                </Banner>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Live Preview</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  This is exactly what shoppers see on your storefront. Click the button below for a test spin —
                  no discount code is issued from here.
                </Text>
                <LivePreview settings={form} />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Recent Spins ({entriesCount})</Text>
                {entries.length === 0 ? (
                  <Text as="p" tone="subdued">No one has spun the wheel yet.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Email', 'Prize', 'Code', 'When']}
                    rows={entries.map((e) => [
                      e.email,
                      e.segment_label,
                      e.discount_code ?? '—',
                      e.created_at ? new Date(e.created_at).toLocaleDateString() : '—',
                    ])}
                  />
                )}
                {entriesLastPage > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={entriesPage > 1}
                      onPrevious={() => fetchEntries(entriesPage - 1)}
                      hasNext={entriesPage < entriesLastPage}
                      onNext={() => fetchEntries(entriesPage + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
                <Text as="p" tone="subdued">
                  Go to <strong>Online Store &rarr; Themes &rarr; Customize &rarr; App embeds</strong>, then enable{' '}
                  <strong>Spin-to-Win Popup</strong>. This is a one-time toggle per theme.
                </Text>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
